/**
 * RSVP / waitlist view for programs that don't have a full sales page yet.
 *
 * Rendered by ProgramSalesPage (route /programs/:slug) whenever the slug has
 * no entry in its sales-content registry — i.e. the program exists on the
 * engine as an RSVP shell (cohort status pre_open, no dates, no pricing).
 *
 * The parent has already fetched /api/public/programs/:slug, applied the
 * program palette + fonts to the DOM (--prog-* / --font-* custom properties),
 * loaded Google Fonts, and set the document title — this component only
 * renders chrome + the waitlist capture form.
 *
 * Submit → POST /api/programs/:slug/waitlist (idempotent upsert; the engine's
 * waitlist priority window takes over when the first intake flips to open).
 */

import { useState } from 'react';
import type { ProgramPublicResponse } from './ProgramSalesPage';
import { CountrySelect } from './components/CountrySelect';
import { SmsOptIn } from './components/SmsOptIn';
import { PolicyLinks } from './components/PolicyLinks';

const API = import.meta.env.VITE_API_BASE_URL as string;

export default function ProgramRsvpView({
  slug,
  data,
}: {
  slug: string;
  data: ProgramPublicResponse;
}) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [step, setStep] = useState<'email' | 'details'>('email');
  const [state, setState] = useState<'form' | 'submitting' | 'success' | 'error'>('form');
  const [errMsg, setErrMsg] = useState('');

  const title = data.program.display_name ?? data.program.name;
  const description = data.program.description;

  const postWaitlist = async (body: Record<string, unknown>): Promise<boolean> => {
    const res = await fetch(`${API}/api/programs/${encodeURIComponent(slug)}/waitlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: 'discover-program-rsvp', ...body }),
    });
    if (!res.ok) {
      const b = (await res.json().catch(() => null)) as { error?: string } | null;
      setState('error');
      setErrMsg(
        res.status === 422
          ? "That email doesn't look deliverable — double-check the spelling?"
          : (b?.error ?? "Something went wrong. Give it another try?"),
      );
      return false;
    }
    return true;
  };

  // Step 1 — email only. The RSVP is saved the moment it lands.
  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) { setState('error'); setErrMsg('Enter your email so we know where to reach you.'); return; }
    setState('submitting'); setErrMsg('');
    try {
      if (await postWaitlist({ email: trimmed })) { setState('form'); setStep('details'); }
    } catch { setState('error'); setErrMsg("Something went wrong. Give it another try?"); }
  };

  // Step 2 — first name + last name + country (COALESCEd onto the same
  // entry). All three are required — the minimum every opt-in on the
  // engine captures alongside email (2026-09-13, Michael).
  const detailsComplete = firstName.trim() && lastName.trim() && country;
  const submitDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailsComplete) return;
    setState('submitting'); setErrMsg('');
    try {
      if (await postWaitlist({ email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim(), country })) {
        setState('success');
      }
    } catch { setState('error'); setErrMsg("Something went wrong. Give it another try?"); }
  };

  return (
    <div style={pageWrap}>
      {/* ── Top nav ── */}
      <header style={topNav}>
        <div style={topNavInner}>
          {data.brand?.logoUrl && (
            <a href="https://www.thesuccessfulbookkeeper.com" style={topNavLogoLink}>
              <img src={data.brand.logoUrl} alt={data.brand.name} style={topNavLogo} />
            </a>
          )}
          <a href={`/lets-talk/${slug}`} style={topNavLink}>
            Talk with our team
          </a>
        </div>
      </header>

      {/* ── Hero + RSVP card ── */}
      <section style={heroSection}>
        <div style={heroInner}>
          {(data.brand?.logoWhiteUrl || data.brand?.logoUrl) && (
            <img
              src={data.brand.logoWhiteUrl ?? data.brand.logoUrl!}
              alt={data.brand.name}
              style={data.brand?.logoWhiteUrl ? heroLogo : heroLogoFiltered}
            />
          )}
          <p style={eyebrow}>
            <span style={eyebrowDot}>●</span> Enrollment opens soon
          </p>
          <h1 style={heroH1}>{title}</h1>
          {description && <p style={heroSub}>{description}</p>}

          <div style={rsvpCard}>
            {state === 'success' ? (
              <>
                <h2 style={cardHead}>You're on the list.</h2>
                <p style={cardSub}>
                  We'll email you the moment enrollment opens — and you'll hear before anyone
                  else does.
                </p>
                <SmsOptIn
                  email={email || null}
                  firstName={firstName || null}
                  source="program-register"
                  sourceDetail={slug}
                  heading="Want a text when doors open?"
                />
              </>
            ) : (
              <>
                <h2 style={cardHead}>Be first in line</h2>
                <p style={cardSub}>
                  The next intake isn't scheduled yet. Leave your name and we'll let you know the
                  moment doors open — RSVP'd folks hear first.
                </p>
                {step === 'email' ? (
                  <form onSubmit={submitEmail} style={formRow}>
                    <input
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={inputStyle}
                      disabled={state === 'submitting'}
                      autoFocus
                      required
                    />
                    <button type="submit" style={submitBtn} disabled={state === 'submitting'}>
                      {state === 'submitting' ? 'One moment…' : 'RSVP for the next intake →'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={submitDetails} style={formRow}>
                    <input
                      type="text"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      style={inputStyle}
                      disabled={state === 'submitting'}
                      autoFocus
                      required
                    />
                    <input
                      type="text"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      style={inputStyle}
                      disabled={state === 'submitting'}
                      required
                    />
                    <CountrySelect value={country} onChange={setCountry} style={inputStyle} />
                    <button type="submit" style={submitBtn} disabled={state === 'submitting' || !detailsComplete}>
                      {state === 'submitting' ? 'Saving…' : 'Done →'}
                    </button>
                  </form>
                )}
                {state === 'error' && errMsg && <p style={errStyle}>{errMsg}</p>}
              </>
            )}
          </div>

          <p style={finePrint}>
            Questions before then?{' '}
            <a
              href={`/lets-talk/${slug}`}
              style={finePrintLink}
            >
              Talk with our team
            </a>{' '}
            — no obligation, just a conversation.
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={footer}>
        <a href="https://www.thesuccessfulbookkeeper.com" style={footerLink}>
          The Successful Bookkeeper
        </a>
        <span style={footerSep}>·</span>
        <span>Built for Freedom.</span>
        <div style={{ marginTop: 8 }}><PolicyLinks /></div>
      </footer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Inline styles — same --prog-* / --font-* conventions as ProgramSalesPage
// ────────────────────────────────────────────────────────────────────────────

const PROG_PRIMARY = 'var(--prog-primary, #FF6B35)';
const PROG_DEEP = 'var(--prog-deep, #1A1A3E)';
const PROG_TEXT_DARK = 'var(--prog-text-dark, #0A0A2E)';
const FONT_HEADING = 'var(--font-heading, "Poppins", -apple-system, sans-serif)';
const FONT_BODY = 'var(--font-body, "Poppins", -apple-system, sans-serif)';

const pageWrap: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background: '#FFFFFF',
  color: PROG_TEXT_DARK,
  fontFamily: FONT_BODY,
  fontSize: '17px',
  lineHeight: 1.6,
};

const topNav: React.CSSProperties = {
  background: '#FFFFFF',
  borderBottom: '1px solid rgba(10,10,46,0.08)',
  padding: '12px 24px',
};
const topNavInner: React.CSSProperties = {
  maxWidth: '1100px',
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
};
const topNavLogoLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center' };
const topNavLogo: React.CSSProperties = { height: '36px', width: 'auto', display: 'block' };
const topNavLink: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: '13px',
  fontWeight: 600,
  color: PROG_PRIMARY,
  textDecoration: 'none',
  borderBottom: `2px solid ${PROG_PRIMARY}`,
  paddingBottom: '1px',
};

const heroSection: React.CSSProperties = {
  flex: 1,
  background: `linear-gradient(160deg, ${PROG_TEXT_DARK} 0%, ${PROG_DEEP} 100%)`,
  color: '#fff',
  padding: '88px 24px 96px',
};
const heroInner: React.CSSProperties = { maxWidth: '720px', margin: '0 auto', textAlign: 'center' };
const heroLogo: React.CSSProperties = { maxHeight: '48px', marginBottom: '28px' };
const heroLogoFiltered: React.CSSProperties = { ...heroLogo, filter: 'brightness(0) invert(1)' };
const eyebrow: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  opacity: 0.9,
  margin: '0 0 18px',
};
const eyebrowDot: React.CSSProperties = { color: PROG_PRIMARY, fontSize: '10px', marginRight: '8px' };
const heroH1: React.CSSProperties = {
  fontFamily: FONT_HEADING,
  fontSize: 'clamp(34px, 5vw, 52px)',
  fontWeight: 800,
  lineHeight: 1.12,
  margin: '0 0 20px',
};
const heroSub: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: '18px',
  lineHeight: 1.65,
  opacity: 0.88,
  margin: '0 auto 40px',
  maxWidth: '600px',
};

const rsvpCard: React.CSSProperties = {
  background: '#FFFFFF',
  color: PROG_TEXT_DARK,
  borderRadius: '14px',
  padding: '32px 32px 36px',
  maxWidth: '520px',
  margin: '0 auto',
  boxShadow: '0 18px 48px rgba(0,0,0,0.28)',
  textAlign: 'center',
};
const cardHead: React.CSSProperties = {
  fontFamily: FONT_HEADING,
  fontSize: '24px',
  fontWeight: 700,
  margin: '0 0 10px',
};
const cardSub: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: '15px',
  lineHeight: 1.6,
  opacity: 0.8,
  margin: '0 0 22px',
};
const formRow: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};
const inputStyle: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: '16px',
  padding: '13px 16px',
  borderRadius: '8px',
  border: '1px solid rgba(10,10,46,0.18)',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};
const submitBtn: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: '16px',
  fontWeight: 700,
  color: '#fff',
  background: PROG_TEXT_DARK,
  border: 'none',
  borderRadius: '8px',
  padding: '14px 20px',
  cursor: 'pointer',
};
const errStyle: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: '14px',
  color: '#B84040',
  margin: '14px 0 0',
};

const finePrint: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: '14px',
  opacity: 0.75,
  margin: '28px 0 0',
};
const finePrintLink: React.CSSProperties = {
  color: '#fff',
  fontWeight: 600,
  textDecoration: 'underline',
};

const footer: React.CSSProperties = {
  background: PROG_TEXT_DARK,
  color: 'rgba(255,255,255,0.7)',
  fontFamily: FONT_BODY,
  fontSize: '13px',
  textAlign: 'center',
  padding: '18px 24px',
  borderTop: '1px solid rgba(255,255,255,0.08)',
};
const footerLink: React.CSSProperties = { color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontWeight: 600 };
const footerSep: React.CSSProperties = { margin: '0 8px', opacity: 0.5 };
